import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useRecoilState } from 'recoil';
import { leftSideBarState } from 'recoils/common/menuState';

import { findActiveLnb } from 'utils/menu/lnbUtil';
import { clickToGTM } from 'utils/seo/seoGTMUtil';

/**
 * @function useLeftSideBar
 * @desc LeftSideBar 로직을 담당하는 커스텀 훅.
 * @returns {object} LeftSideBar 관련 함수와 상태
 */
const useLeftSideBar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [lnbState, setLnbState] = useRecoilState(leftSideBarState);
  const [expandedItems, setExpandedItems] = useState({});

  // 경로 변경 시 LNB 데이터 계산 및 업데이트
  useEffect(() => {
    const activeLnb = findActiveLnb(location.pathname);
    // lnbState가 존재하고 타이틀이 같다면 업데이트 하지 않음 (같은 대분류 내 이동 시 사이드바 리렌더링 방지)
    if (lnbState && lnbState.title === activeLnb.title) {
      return;
    }
    setLnbState(activeLnb);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, setLnbState]);

  // lnbState가 업데이트될 때마다 모든 subContent를 가진 항목을 기본적으로 펼침 상태로 초기화
  useEffect(() => {
    if (lnbState?.lnb) {
      const initialStates = {};
      lnbState.lnb.forEach((item, index) => {
        if (item.content && item.content.length > 0) {
          initialStates[index] = true;
        }
      });
      setExpandedItems(initialStates);
    }
  }, [lnbState]);

  /**
   * @desc 메뉴 항목 클릭 핸들러
   * @param {string} path - 이동할 경로
   */
  const onMenuClick = useCallback(
    (menu) => {
      if (menu.path) {
        clickToGTM({
          target: menu.title,
          id: 'left-sidebar-click',
          url: window.location.host + menu.path,
        });
        navigate(menu.path);
      }
    },
    [navigate]
  );

  const onToggleExpand = (e, index) => {
    e.stopPropagation();
    setExpandedItems((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const onNewTabClick = (path) => {
    window.open(path, '_blank', 'noopener, noreferrer');
  };

  return {
    location,
    lnbState,
    expandedItems,
    onMenuClick,
    onNewTabClick,
    onToggleExpand,
  };
};

export default useLeftSideBar;
